// AssessmentInputs.vue のためのリアクティブ・グルー。
// ステータス遷移の判断は assessmentStateMachine（純粋関数）へ委譲し、
// ここでは Vue のリアクティブ状態・watcher・emit の配線だけを担う。

import { computed, reactive, ref, watch } from "vue";
import {
  complaintCommitValue,
  confirmComplaint,
  confirmDiagnosis,
  diagnosisCommitValue,
  emptyDiagnosisValue,
  evaluateComplaintStatus,
  evaluateDiagnosisStatus,
  initComplaintState,
  initDiagnosisState,
  isDone,
  resolveComplaintFromPreprocessing,
  resolveDiagnosisFromPreprocessing,
} from "./assessmentStateMachine";
import type {
  AssessmentAnswer,
  AssessmentStatus,
  ComplaintState,
  ConsultationAssessmentDraft,
  DiagnosisCandidate,
  DiagnosisId,
  DiagnosisState,
  DiagnosisValue,
} from "./types";

export interface AssessmentInputsProps {
  answer: AssessmentAnswer;
  departmentType?: string;
  consultationAssessmentDraft?: ConsultationAssessmentDraft | null;
  preprocessingStatus?: string;
}

export type AssessmentInputsEmit = {
  (event: "update:complaint", value: string): void;
  (event: "update:diagnosis", value: DiagnosisValue): void;
};

export function useAssessmentInputs(props: AssessmentInputsProps, emit: AssessmentInputsEmit) {
  const isComplaintFocused = ref(false);

  // 主訴・暫定診断の表示値はローカルに保持し、store answer とは間接的に結びつける。
  // AI提案（draft）を表示している間は store answer は空のまま。
  const complaintStatus = ref<AssessmentStatus>("parsing");
  const localComplaint = ref("");
  const suggestedComplaintText = ref<string | null>(null);

  const diagnosisStatus = ref<AssessmentStatus>("parsing");
  // 子 Diagnosis / MidwifeDiagnosis はこのオブジェクトを直接 mutate する。
  const localDiagnosisKarte = reactive<DiagnosisValue>(emptyDiagnosisValue());
  const suggestedDiagnosisBranchId = ref<DiagnosisId>(null);
  const suggestedDiagnosisName = ref<string | null>(null);

  // --- 純粋な状態オブジェクト <-> リアクティブ ref の相互変換 ---
  function complaintState(): ComplaintState {
    return {
      status: complaintStatus.value,
      value: localComplaint.value,
      suggestedText: suggestedComplaintText.value,
    };
  }
  function applyComplaintState(state: ComplaintState): void {
    complaintStatus.value = state.status;
    localComplaint.value = state.value;
    suggestedComplaintText.value = state.suggestedText;
  }
  function diagnosisState(): DiagnosisState {
    return {
      status: diagnosisStatus.value,
      value: { ...localDiagnosisKarte },
      suggestedBranchId: suggestedDiagnosisBranchId.value,
      suggestedName: suggestedDiagnosisName.value,
    };
  }
  function applyDiagnosisValue(value: DiagnosisValue): void {
    localDiagnosisKarte.branch_id = value.branch_id ?? null;
    localDiagnosisKarte.icd10BranchName = value.icd10BranchName ?? null;
    localDiagnosisKarte.original_disease = value.original_disease ?? null;
    localDiagnosisKarte.icd10OriginalDisease = value.icd10OriginalDisease ?? null;
  }
  function applyDiagnosisState(state: DiagnosisState): void {
    diagnosisStatus.value = state.status;
    suggestedDiagnosisBranchId.value = state.suggestedBranchId;
    suggestedDiagnosisName.value = state.suggestedName;
    applyDiagnosisValue(state.value);
  }

  // --- store answer へのコミット（差分があるときだけ emit） ---
  function commitComplaint(): void {
    const target = complaintCommitValue(complaintState());
    if ((props.answer?.complaint ?? "") !== target) {
      emit("update:complaint", target);
    }
  }
  function commitDiagnosis(): void {
    const target = diagnosisCommitValue(diagnosisState());
    const differs =
      (props.answer?.branch_id ?? null) !== (target.branch_id ?? null) ||
      (props.answer?.original_disease ?? null) !== (target.original_disease ?? null) ||
      (props.answer?.icd10BranchName ?? null) !== (target.icd10BranchName ?? null) ||
      (props.answer?.icd10OriginalDisease ?? null) !== (target.icd10OriginalDisease ?? null);
    if (differs) {
      emit("update:diagnosis", target);
    }
  }

  // --- 初期化・解決 ---
  function initializeFromAnswer(): void {
    applyComplaintState(initComplaintState(props.answer));
    applyDiagnosisState(initDiagnosisState(props.answer));
  }
  function resolveFromPreprocessing(): void {
    applyComplaintState(
      resolveComplaintFromPreprocessing(
        complaintState(),
        props.preprocessingStatus ?? "idle",
        props.consultationAssessmentDraft
      )
    );
    applyDiagnosisState(
      resolveDiagnosisFromPreprocessing(
        diagnosisState(),
        props.preprocessingStatus ?? "idle",
        props.consultationAssessmentDraft
      )
    );
  }

  // --- 主訴の操作 ---
  function onComplaintFocus(): void {
    isComplaintFocused.value = true;
  }
  function onComplaintBlur(): void {
    isComplaintFocused.value = false;
  }
  function onComplaintModelUpdate(value: string): void {
    applyComplaintState(evaluateComplaintStatus(complaintState(), value));
    commitComplaint();
  }
  function onComplaintConfirm(): void {
    applyComplaintState(confirmComplaint(complaintState()));
    commitComplaint();
  }

  // --- 暫定診断の操作 ---
  function onDiagnosisChanged(): void {
    applyDiagnosisState(evaluateDiagnosisStatus(diagnosisState(), { ...localDiagnosisKarte }));
    commitDiagnosis();
  }
  function onDiagnosisConfirm(): void {
    applyDiagnosisState(confirmDiagnosis(diagnosisState()));
    commitDiagnosis();
  }
  function onDiagnosisCandidateClick(candidate: DiagnosisCandidate | null | undefined): void {
    if (!props.answer.editable) return;
    if (!candidate) return;
    // ローカルへ反映（localDiagnosisKarte watcher が onDiagnosisChanged を呼ぶ）
    applyDiagnosisValue({
      branch_id: candidate.id,
      icd10BranchName: candidate.name,
      original_disease: null,
      icd10OriginalDisease: null,
    });
  }

  // --- 表示用 computed ---
  // 採用ボタンは編集権限がある（answer.editable）操作者にのみ表示する。
  const isComplaintSuggested = computed(
    () => !!props.answer.editable && complaintStatus.value === "suggested"
  );
  const isDiagnosisSuggested = computed(
    () => !!props.answer.editable && diagnosisStatus.value === "suggested"
  );
  const isDiagnosisDone = computed(() => isDone(diagnosisStatus.value));

  const hasAnySuggestedAssessment = computed(
    () => isComplaintSuggested.value || isDiagnosisSuggested.value
  );
  // 採用案内の表示可否。suggested が残っているときだけ表示する
  // （parsing 中は対象外、suggested が 0 個になったら非表示）。
  const showSuggestionNotice = computed(() => hasAnySuggestedAssessment.value);

  const assessmentUrgency = computed(() => props.consultationAssessmentDraft?.urgency ?? null);
  const assessmentBody = computed(() => props.consultationAssessmentDraft?.body ?? "");
  const shouldShowUrgencyWarning = computed(
    () => assessmentUrgency.value === "immediate" || assessmentUrgency.value === 1
  );

  const otherDiagnosisCandidates = computed<DiagnosisCandidate[]>(() => {
    const candidates = props.consultationAssessmentDraft?.icd10BranchCandidates ?? [];
    const currentDiagnosisName = (localDiagnosisKarte.icd10BranchName || "").trim();
    if (!currentDiagnosisName) return candidates;
    return candidates.filter((candidate) => candidate?.name !== currentDiagnosisName);
  });

  // --- watcher ---
  // store answer が差し替わったとき（reopen / 下書き保存後の再取得）に復元する。
  // フィールドの直接 mutate では参照が変わらないため、このwatcherは発火しない。
  watch(
    () => props.answer,
    (newAnswer, oldAnswer) => {
      if (newAnswer !== oldAnswer) {
        initializeFromAnswer();
        resolveFromPreprocessing();
      }
    }
  );
  watch(
    () => props.preprocessingStatus,
    () => resolveFromPreprocessing()
  );
  watch(
    () => props.consultationAssessmentDraft,
    () => resolveFromPreprocessing(),
    { deep: true }
  );
  // 子 Diagnosis / MidwifeDiagnosis は localDiagnosisKarte を直接 mutate するため、
  // 深い監視で変更を検知してステータス再評価・コミットする。
  watch(localDiagnosisKarte, () => onDiagnosisChanged(), { deep: true });

  // 初期化（Options API の created 相当）。
  initializeFromAnswer();
  resolveFromPreprocessing();

  return {
    // state
    isComplaintFocused,
    localComplaint,
    localDiagnosisKarte,
    complaintStatus,
    diagnosisStatus,
    // computed
    isComplaintSuggested,
    isDiagnosisSuggested,
    isDiagnosisDone,
    showSuggestionNotice,
    assessmentBody,
    shouldShowUrgencyWarning,
    otherDiagnosisCandidates,
    // handlers
    onComplaintFocus,
    onComplaintBlur,
    onComplaintModelUpdate,
    onComplaintConfirm,
    onDiagnosisConfirm,
    onDiagnosisCandidateClick,
  };
}
